/**
 * What search engines and link previews read about each Games page: its
 * title, description, canonical address, and structured data (schema.org
 * JSON-LD). One source for the app (which sets them as the page changes) and
 * the static snapshots, so the two cannot describe a page differently.
 */
import { gameCount } from './format';
import {
  GAMES_HOME_PATH,
  GAMES_MOST_PLAYED_PATH,
  gamePagePath,
  gamesArchePath,
  gamesGenrePath,
} from './routes';
import type {
  GameArchePageData,
  GameDetailData,
  GameGenrePageData,
  GamesHomeData,
  GamesMostPlayedData,
} from './types';

export interface PageSeo {
  title: string;
  description: string;
  /** Path of the canonical address; joined to the site origin by the caller. */
  canonicalPath: string;
  /** schema.org objects, one `<script type="application/ld+json">` each. */
  jsonLd: object[];
  /** Image for link previews (absolute URL), when the page has one. */
  image?: string;
}

const SITE = 'Fias';
const DESCRIPTION_MAX = 160;

export function clip(text: string, max = DESCRIPTION_MAX): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:]+$/, '')}…`;
}

export function absolute(origin: string, path: string): string {
  return `${origin.replace(/\/+$/, '')}${path}`;
}

export function breadcrumbs(origin: string, trail: Array<{ name: string; path: string }>): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((step, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: step.name,
      item: absolute(origin, step.path),
    })),
  };
}

function itemList(origin: string, games: Array<{ slug: string; title: string }>): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: games.map((game, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: game.title,
      url: absolute(origin, gamePagePath(game.slug)),
    })),
  };
}

export function gamesHomeSeo(data: GamesHomeData, origin: string): PageSeo {
  const top = data.top.slice(0, 20);
  return {
    title: `Games — play in your browser | ${SITE}`,
    description:
      'Word, puzzle, board, card and arcade games you play right in your browser. See what’s new, the most played, and every genre on Fias.',
    canonicalPath: GAMES_HOME_PATH,
    jsonLd: top.length > 0 ? [itemList(origin, top)] : [],
  };
}

export function gamesGenreSeo(data: GameGenrePageData, origin: string): PageSeo {
  const { genre, games } = data;
  const examples = games
    .slice(0, 3)
    .map((g) => g.title)
    .join(', ');
  return {
    title: `${genre.label} games — play in your browser | ${SITE}`,
    description: clip(
      `${gameCount(genre.count)} in ${genre.label.toLowerCase()} on Fias${
        examples ? `, including ${examples}` : ''
      }. Play in your browser.`,
    ),
    canonicalPath: gamesGenrePath(genre.id),
    jsonLd: [
      breadcrumbs(origin, [
        { name: 'Games', path: GAMES_HOME_PATH },
        ...(genre.parent
          ? [{ name: genre.parent.label, path: gamesGenrePath(genre.parent.id) }]
          : []),
        { name: genre.label, path: gamesGenrePath(genre.id) },
      ]),
      ...(games.length > 0 ? [itemList(origin, games)] : []),
    ],
  };
}

export function gamesArcheSeo(data: GameArchePageData, origin: string): PageSeo {
  const { arche, games } = data;
  const examples = games
    .slice(0, 3)
    .map((g) => g.title)
    .join(', ');
  const path = gamesArchePath(arche.key);
  return {
    title: `Games in ${arche.name} — play in your browser | ${SITE}`,
    description: clip(
      `${gameCount(data.total)} in ${arche.name} on Fias${
        examples ? `, including ${examples}` : ''
      }. Play in your browser.`,
    ),
    canonicalPath: path,
    jsonLd: [
      breadcrumbs(origin, [
        { name: 'Games', path: GAMES_HOME_PATH },
        { name: arche.name, path },
      ]),
      ...(games.length > 0 ? [itemList(origin, games)] : []),
    ],
  };
}

export function gamesMostPlayedSeo(data: GamesMostPlayedData, origin: string): PageSeo {
  const examples = data.games
    .slice(0, 3)
    .map((g) => g.title)
    .join(', ');
  return {
    title: `Most played games — play in your browser | ${SITE}`,
    description: clip(
      `The games played most on Fias${examples ? `, led by ${examples}` : ''}. Play in your browser.`,
    ),
    canonicalPath: GAMES_MOST_PLAYED_PATH,
    jsonLd: [
      breadcrumbs(origin, [
        { name: 'Games', path: GAMES_HOME_PATH },
        { name: 'Most played', path: GAMES_MOST_PLAYED_PATH },
      ]),
      ...(data.games.length > 0 ? [itemList(origin, data.games)] : []),
    ],
  };
}

export function gameSeo(
  data: GameDetailData,
  origin: string,
  genreLabels: Readonly<Record<string, string>> = {},
): PageSeo {
  const { game } = data;
  const path = gamePagePath(game.slug);
  const image = game.iconUrlLarge ?? game.iconUrl ?? undefined;
  const labels = game.genres.map((g) => genreLabels[g] ?? g);
  const firstGenre = game.genres[0];
  return {
    title:
      game.kind === 'listing'
        ? `${game.title} — play in ${game.archeName} | ${SITE}`
        : `${game.title} — play in your browser | ${SITE}`,
    description: clip(game.description || game.about),
    canonicalPath: path,
    image,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'VideoGame',
        name: game.title,
        description: clip(game.about || game.description, 500),
        url: absolute(origin, path),
        ...(image ? { image } : {}),
        ...(labels.length > 0 ? { genre: labels } : {}),
        gamePlatform: 'Web browser',
        applicationCategory: 'Game',
        operatingSystem: 'Any',
        ...(game.kind === 'listing'
          ? { isPartOf: { '@type': 'VideoGame', name: game.archeName } }
          : {}),
      },
      breadcrumbs(origin, [
        { name: 'Games', path: GAMES_HOME_PATH },
        ...(firstGenre
          ? [{ name: genreLabels[firstGenre] ?? firstGenre, path: gamesGenrePath(firstGenre) }]
          : []),
        { name: game.title, path },
      ]),
    ],
  };
}

/**
 * JSON for inside a `<script>` element. `JSON.stringify` alone is not safe
 * there: a `</script>` in a game's description would end the element, and
 * U+2028/U+2029 break older parsers. Every `<`, `>` and `&` and both line
 * separators are written as JSON unicode escapes, which parse back unchanged.
 */
export function serializeJsonForScript(value: unknown): string {
  const backslash = String.fromCharCode(92);
  const unsafe = new RegExp(`[<>&${String.fromCharCode(0x2028, 0x2029)}]`, 'g');
  return JSON.stringify(value).replace(
    unsafe,
    (c) => `${backslash}u${c.charCodeAt(0).toString(16).padStart(4, '0')}`,
  );
}
